import { take, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { AppRoutes } from '@app/app.routes';
import { BaseLeadRequestDetailComponent } from '@leads/client-facing/classes/base-lead-request-detail.component';
import {
	ScheduledAssessmentEmployeeIdChange,
	ScheduledAssessmentJobDescriptionChange,
	ScheduledAssessmentNotesChange,
	ScheduledAssessmentStatusChange,
} from '@leads/client-facing/classes/base-scheduled-item-detail.component';
import { LeadAddPropertyModalComponent } from '@leads/client-facing/components/lead-add-property-modal/lead-add-property-modal.component';
import { ScheduledItemActionsComponent } from '@leads/client-facing/components/scheduled-item-actions/scheduled-item-actions.component';
import { ScheduledItemTitleComponent } from '@leads/client-facing/components/scheduled-item-title/scheduled-item-title.component';
import { ILeadAddProperty, PropertySelectType } from '@leads/client-facing/models/domain/lead-request.domain';
import { LeadRequestType } from '@leads/shared/models/domain/lead-request.domain';
import { ScheduledAssessment } from '@leads/shared/models/domain/scheduled-assessment.domain';
import { AccordionItemComponent } from '@shared/components/ui/accordion-item/accordion-item.component';
import { AccordionComponent } from '@shared/components/ui/accordion/accordion.component';

import { MobileScheduledItemCreateOffcanvasComponent } from '../mobile-scheduled-item-create-offcanvas/mobile-scheduled-item-create-offcanvas.component';
import { MobileScheduledItemDetailOffcanvasComponent } from '../mobile-scheduled-item-detail-offcanvas/mobile-scheduled-item-detail-offcanvas.component';

@Component({
	selector: 'app-mobile-scheduled-items-list-offcanvas',
	imports: [
		CommonModule,
		AccordionComponent,
		AccordionItemComponent,
		AccordionComponent,
		AccordionItemComponent,
		ScheduledItemActionsComponent,
		ScheduledItemTitleComponent,
	],
	templateUrl: './mobile-scheduled-items-list-offcanvas.component.html',
	styleUrl: './mobile-scheduled-items-list-offcanvas.component.scss',
	host: {
		class: 'h-100',
		style: 'display: contents',
	},
})
export class MobileScheduledItemsListOffcanvasComponent extends BaseLeadRequestDetailComponent {
	close(): void {
		this.offcanvasService.dismissLast();
	}

	openScheduler() {
		this.modalService.close();
		this.router.navigate([AppRoutes.SCHEDULE]);
	}

	override addCallAssessment(): void {
		this.openCreateScheduledItemModal(LeadRequestType.CALL);
	}

	override addOnSiteAssessment(): void {
		if (!this.leadRequest.personalDetails.installationAddress?.fullAddress) {
			const modalRef = this.modalService.open(LeadAddPropertyModalComponent, false, 'md');
			const componentInstance = modalRef.componentInstance as LeadAddPropertyModalComponent;
			componentInstance.clientProperties = this.client.properties;
			componentInstance.showContextAlert = true;
			componentInstance.propertySelected.pipe(take(1)).subscribe((propertySelected: ILeadAddProperty) => {
				this.leadRequestService.updateInstallationAddress(this.leadRequest.id!, propertySelected.selectedClientProperty);
				if (propertySelected.propertySelectType === PropertySelectType.NEW) {
					this.clientService.addClientProperty(this.client.client.id!, propertySelected.selectedClientProperty);
				}
				this.openCreateScheduledItemModal(LeadRequestType.ON_SITE_VISIT);
			});
		} else {
			this.openCreateScheduledItemModal(LeadRequestType.ON_SITE_VISIT);
		}
	}

	private openCreateScheduledItemModal(leadRequestType: LeadRequestType) {
		const ref = this.offcanvasService.open(MobileScheduledItemCreateOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as MobileScheduledItemCreateOffcanvasComponent;
		componentInstance.scheduledItemRequestType = leadRequestType;
		componentInstance.selectedOrganisationRef = this.selectedOrganisationRef;
		componentInstance.employees = this.employees;
		componentInstance.activeOrganisationUsers = this.activeOrganisationUsers;
		componentInstance.client = this.client.client;
		componentInstance.leadRequest = this.leadRequest;
		componentInstance.disabledDaysOfTheWeek = this.disabledDaysOfTheWeek;
		componentInstance.serviceTypes = this.serviceTypes;
		componentInstance.createdAssessment.pipe(take(1)).subscribe((assessment: ScheduledAssessment) => {
			this.addAssessment(assessment);
		});
	}

	openScheduledItemsListItemModal(item: ScheduledAssessment, index: number): void {
		const ref = this.offcanvasService.open(MobileScheduledItemDetailOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as MobileScheduledItemDetailOffcanvasComponent;
		componentInstance.index = index;
		componentInstance.isSavingNotes = this.isSavingNotes;
		componentInstance.isSavingJobDescription = this.isSavingJobDescription;
		componentInstance.scheduledItem = item;
		componentInstance.client = this.client.client;
		componentInstance.readonlyView = this.readonlyView;
		componentInstance.employees = this.employees;
		componentInstance.statusChanged.pipe(takeUntil(this.destroyed$)).subscribe(($event: ScheduledAssessmentStatusChange) => {
			this.statusChanged($event);
		});
		componentInstance.employeeIdChanged
			.pipe(takeUntil(this.destroyed$))
			.subscribe(($event: ScheduledAssessmentEmployeeIdChange) => {
				this.employeeIdChanged($event);
			});
		componentInstance.assessmentNotesChanged
			.pipe(takeUntil(this.destroyed$))
			.subscribe(($event: ScheduledAssessmentNotesChange) => {
				this.assessmentNotesChanged($event);
			});
		componentInstance.jobDescriptionChanged
			.pipe(takeUntil(this.destroyed$))
			.subscribe(($event: ScheduledAssessmentJobDescriptionChange) => {
				this.jobDescriptionChanged($event);
			});
	}
}
